/** Экран партии.
 *
 * Иерархия строгая и одинаковая на всех ширинах: доска — единственный
 * крупный объект по центру, над ней только информация (уровень, баланс,
 * ошибки, таймер), под ней только действия — панель инструментов, затем
 * клавиатура. Рука не пересекает доску, и это важнее компактности.
 *
 * Аварийные сообщения появляются полосой над доской и сдвигают её вниз,
 * но никогда не всплывают поверх цифр: закрытая цифра — потерянный ход.
 */

import { useCallback, useEffect, useState } from 'react'

import { ApiError } from '../api/client'
import { fetchHintPrices, fillAutoNotes, pauseGame } from '../api/endpoints'
import type { GameState, HintKind, MoveKind } from '../api/types'
import { Board } from '../components/Board'
import { HintSheet, type SheetAction } from '../components/HintSheet'
import { Keypad } from '../components/Keypad'
import { LossPanel, WinPanel } from '../components/ResultPanel'
import { Dialog } from '../components/Sheet'
import { Toolbar } from '../components/Toolbar'
import { Crystal, LevelMark } from '../components/ui'
import { EMPTY, isGiven } from '../game/board'
import { formatTime, hoursUntil } from '../game/format'
import { play as playSound } from '../game/sound'
import { useElapsed, useGame } from '../game/useGame'
import { t } from '../i18n'
import { backButton, haptic } from '../telegram/webapp'

const HARDCORE_LIMIT = 3
/** Рамка подсказки держится три секунды — столько же, сколько анимация. */
const HINT_GLOW_MS = 3000

export interface GameScreenProps {
  initialGame: GameState
  balance: number
  /** Настройки игрока: они меняют способ ввода и поведение заметок. */
  digitFirst: boolean
  autoClearNotes: boolean
  /** Потолок награды за задачу дня — им объясняется, где взять кристаллы. */
  dailyReward: number
  previousBest: number | null
  onBalanceChange: (balance: number) => void
  onExit: () => void
  onPlayAgain: () => void
  onRestart: (game: GameState) => void
  onOpenCrystals: () => void
  onShare: (game: GameState) => void
}

export function GameScreen({
  initialGame,
  balance,
  digitFirst,
  autoClearNotes,
  dailyReward,
  previousBest,
  onBalanceChange,
  onExit,
  onPlayAgain,
  onRestart,
  onOpenCrystals,
  onShare,
}: GameScreenProps) {
  const controller = useGame(initialGame, { autoClearNotes })
  const { game, snapshot } = controller
  const elapsed = useElapsed(game)

  const [selected, setSelected] = useState<number | null>(null)
  const [selectedDigit, setSelectedDigit] = useState<number | null>(null)
  const [pinnedDigit, setPinnedDigit] = useState<number | null>(null)
  const [notesMode, setNotesMode] = useState(false)
  const [sheetOpen, setSheetOpen] = useState(false)
  const [prices, setPrices] = useState<Record<SheetAction, number> | null>(null)
  const [freeHint, setFreeHint] = useState({ available: false, hours: 0 })
  const [errorCells, setErrorCells] = useState<number[]>([])
  const [hintedCell, setHintedCell] = useState<number | null>(null)
  const [shortfall, setShortfall] = useState<number | null>(null)
  const [busy, setBusy] = useState(false)

  const finished = game.status !== 'active'
  const locked = finished || game.is_paused
  const hint = controller.hint

  useEffect(() => backButton.show(onExit), [onExit])

  const loadPrices = useCallback(async () => {
    try {
      const response = await fetchHintPrices(game.id)
      setPrices(response.prices as Record<SheetAction, number>)
      setFreeHint({
        available: response.free_hint.available,
        hours: hoursUntil(response.free_hint.resets_at),
      })
    } catch {
      // Прайс-лист не критичен: без него не откроется шторка, но партия
      // продолжается, и ронять её из-за этого нельзя.
    }
  }, [game.id])

  useEffect(() => {
    void loadPrices()
  }, [loadPrices])

  /** Пауза ставится сервером: клиентскому таймеру доверять нельзя. */
  const togglePause = useCallback(async () => {
    try {
      controller.adoptGame(await pauseGame(game.id, game.is_paused))
    } catch {
      // Не вышло — таймер продолжит идти. Это честнее, чем остановить его
      // на экране, оставив идущим на сервере.
    }
  }, [controller, game.id, game.is_paused])

  // Свёрнутый Mini App — та же пауза: без неё каждый перерыв копился бы
  // в зачёт (до потолка на промежуток) и портил таблицу времени.
  useEffect(() => {
    const onHide = () => {
      if (document.visibilityState === 'hidden' && game.status === 'active' && !game.is_paused) {
        void pauseGame(game.id).catch(() => {})
      }
    }
    document.addEventListener('visibilitychange', onHide)
    return () => document.removeEventListener('visibilitychange', onHide)
  }, [game.id, game.is_paused, game.status])

  const play = useCallback(
    (cell: number, kind: MoveKind, value: number) => {
      // Любой ход снимает результат проверки: подсветка ошибок держится до
      // первого исправления, дальше она только спорит с подсветкой выбора.
      setErrorCells([])
      controller.play(cell, kind, value)
      haptic.tap()
      playSound(kind === 'value' ? 'place' : 'erase')
    },
    [controller],
  )

  const putDigit = useCallback(
    (cell: number, digit: number, asNote: boolean) => {
      if (locked || isGiven(game.givens, cell)) return
      if (asNote) {
        const current = snapshot.notes[String(cell)] ?? []
        play(cell, current.includes(digit) ? 'note_remove' : 'note_add', digit)
        return
      }
      // Повторное нажатие той же цифры стирает её: это привычнее, чем
      // тянуться к ластику ради опечатки.
      if (snapshot.board[cell] === String(digit)) play(cell, 'erase', 0)
      else play(cell, 'value', digit)
    },
    [game.givens, locked, play, snapshot],
  )

  const onCellSelect = useCallback(
    (cell: number) => {
      if (locked) return
      haptic.select()

      // Двойной тап по цифре закрепляет её подсветку: так игрок сам ищет
      // указующие пары, а не покупает подсказку. Приложение учит, а не
      // продаёт ответ.
      const digit = snapshot.board[cell]
      if (selected === cell && digit !== EMPTY) {
        setPinnedDigit((current) => (current === Number(digit) ? null : Number(digit)))
        return
      }
      setPinnedDigit(null)
      setSelected(cell)

      // В режиме «сначала цифра» выбор клетки её и заполняет.
      if (digitFirst && selectedDigit !== null) putDigit(cell, selectedDigit, notesMode)
    },
    [digitFirst, locked, notesMode, putDigit, selected, selectedDigit, snapshot.board],
  )

  const onDigit = useCallback(
    (digit: number) => {
      if (locked) return
      if (digitFirst) {
        setSelectedDigit((current) => (current === digit ? null : digit))
        return
      }
      if (selected !== null) putDigit(selected, digit, false)
    },
    [digitFirst, locked, putDigit, selected],
  )

  const onNote = useCallback(
    (digit: number) => {
      if (locked || selected === null) return
      putDigit(selected, digit, true)
    },
    [locked, putDigit, selected],
  )

  const onErase = useCallback(() => {
    if (locked || selected === null) return
    play(selected, snapshot.board[selected] === EMPTY ? 'note_clear' : 'erase', 0)
  }, [locked, play, selected, snapshot.board])

  const buy = useCallback(
    async (action: SheetAction) => {
      setBusy(true)
      try {
        if (action === 'autonotes') {
          const result = await fillAutoNotes(game.id, `${game.id}:autonotes:${game.move_count}`)
          controller.adoptGame(result.game)
          onBalanceChange(result.balance)
        } else {
          onBalanceChange(await controller.askHint(action as HintKind, selected ?? undefined))
        }
        setSheetOpen(false)
        void loadPrices()
      } catch (failure) {
        if (failure instanceof ApiError && failure.code === 'insufficient_funds') {
          setSheetOpen(false)
          setShortfall((failure.body.required ?? 0) - (failure.body.balance ?? 0))
        }
      } finally {
        setBusy(false)
      }
    },
    [controller, game.id, game.move_count, loadPrices, onBalanceChange, selected],
  )

  // Ошибки проверки и клетка, открытая подсказкой, приходят одним ответом:
  // разбираем его здесь, чтобы доска знала, что подсветить.
  useEffect(() => {
    if (!hint) return
    if (hint.kind === 'check') {
      setErrorCells(hint.highlight_cells.slice())
      haptic.notify('warning')
      return
    }
    if (hint.cell !== null) {
      setHintedCell(hint.cell)
      const timer = window.setTimeout(() => setHintedCell(null), HINT_GLOW_MS)
      return () => window.clearTimeout(timer)
    }
  }, [hint])

  useEffect(() => {
    if (controller.mistakeCell !== null) {
      haptic.notify('error')
      playSound('mistake')
    }
  }, [controller.mistakeCell])

  useEffect(() => {
    if (controller.finalScore) {
      haptic.notify('success')
      playSound('win')
    }
  }, [controller.finalScore])

  // Плашка приёма занимает место панели и клавиатуры, а не всплывает над
  // доской: доска должна остаться видна вместе с объяснением.
  const showTechnique = hint !== null && hint.kind !== 'check' && !finished

  return (
    <div className="screen">
      <header className="topbar">
        <button type="button" className="topbar__action" onClick={onExit} aria-label={t('app.back')}>
          ‹
        </button>
        <span className="topbar__title">{t('game.title')}</span>
        <button
          type="button"
          className="topbar__action"
          onClick={() => void togglePause()}
          aria-label={t('game.pause')}
          disabled={finished}
        >
          ⋯
        </button>
      </header>

      <div className="game">
        <div className="game__status">
          <div className="game__status-group">
            <span className="chip">
              <LevelMark difficulty={game.difficulty} />
              {t(`level.${game.difficulty}`)}
            </span>
            <span className="chip">
              <Crystal>{balance}</Crystal>
            </span>
          </div>
          <div className="game__status-group">
            {game.mode === 'hardcore' && (
              <span className="chip chip--error">
                {t('game.mistakes', { used: game.mistakes, limit: HARDCORE_LIMIT })}
              </span>
            )}
            <span
              className={`game__timer num ${game.is_paused ? 'game__timer--paused' : ''}`.trim()}
            >
              {formatTime(elapsed)}
            </span>
          </div>
        </div>

        {controller.offline && (
          <div className="banner">
            <span className="banner__dot" />
            {t('game.offline')}
          </div>
        )}

        {game.is_paused ? (
          <PauseCover
            elapsed={elapsed}
            filled={game.filled_count}
            onResume={() => void togglePause()}
          />
        ) : (
          <Board
            givens={game.givens}
            board={snapshot.board}
            notes={snapshot.notes}
            selected={selected}
            pinnedDigit={pinnedDigit}
            errorCells={errorCells}
            hintedCell={hintedCell}
            highlightCells={showTechnique ? hint.highlight_cells : []}
            shakeCell={controller.mistakeCell}
            onSelect={onCellSelect}
            locked={locked}
          />
        )}

        {showTechnique ? (
          <TechniquePlaque
            title={hint.title ?? ''}
            message={hint.message}
            cell={hint.cell}
            onDismiss={controller.dismissHint}
          />
        ) : (
          <>
            <Toolbar
              canUndo={game.can_undo}
              canRedo={game.can_redo}
              canErase={selected !== null && !isGiven(game.givens, selected)}
              notesMode={notesMode}
              disabled={locked || controller.pendingCount > 0}
              onUndo={controller.undo}
              onRedo={controller.redo}
              onErase={onErase}
              onToggleNotes={() => setNotesMode((mode) => !mode)}
              onHint={() => setSheetOpen(true)}
            />

            <Keypad
              board={snapshot.board}
              notesMode={notesMode}
              selectedDigit={digitFirst ? selectedDigit : null}
              disabled={locked}
              onDigit={onDigit}
              onNote={onNote}
            />
          </>
        )}
      </div>

      {sheetOpen && prices && (
        <HintSheet
          balance={balance}
          prices={prices}
          freeHint={freeHint}
          hasSelection={selected !== null && snapshot.board[selected] === EMPTY}
          dailyReward={dailyReward}
          busy={busy}
          onPick={(action) => void buy(action)}
          onEarn={() => {
            setSheetOpen(false)
            onOpenCrystals()
          }}
          onDismiss={() => setSheetOpen(false)}
        />
      )}

      {shortfall !== null && (
        <Dialog title={t('dialog.funds.title')} onDismiss={() => setShortfall(null)}>
          <p className="hint-text" style={{ margin: 0 }}>
            {t('hints.notEnough', { amount: shortfall })}
          </p>
          <button
            type="button"
            className="button button--primary"
            onClick={() => {
              setShortfall(null)
              onOpenCrystals()
            }}
          >
            {t('dialog.funds.earn')}
          </button>
          <button type="button" className="button" onClick={() => setShortfall(null)}>
            {t('dialog.funds.later')}
          </button>
        </Dialog>
      )}

      {controller.finalScore && (
        <WinPanel
          game={game}
          score={controller.finalScore}
          progress={controller.finalProgress}
          previousBest={previousBest}
          onAgain={onPlayAgain}
          onExit={onExit}
          onShare={() => onShare(game)}
        />
      )}

      {game.status === 'failed' && (
        <LossPanel
          game={game}
          progressCoins={controller.progressCoins}
          onRestart={() => onRestart(game)}
          onExit={onExit}
        />
      )}
    </div>
  )
}

/**
 * Пауза закрывает доску там же, где она была.
 *
 * Ничего вокруг не смещается, поэтому возврат стоит одного тапа, а глаз
 * не ищет заново клетку, на которой игрок остановился.
 */
function PauseCover({
  elapsed,
  filled,
  onResume,
}: {
  elapsed: number
  filled: number
  onResume: () => void
}) {
  return (
    <div className="board-cover">
      <div className="board-cover__bars">
        <span className="board-cover__bar" />
        <span className="board-cover__bar" />
      </div>
      <span style={{ fontSize: 'var(--size-row)', fontWeight: 600, color: 'var(--text-hint)' }}>
        {t('game.pause')}
      </span>
      <span className="hint-text" style={{ maxWidth: 240 }}>
        {t('game.pauseHint', { time: formatTime(elapsed), filled })}
      </span>
      <button type="button" className="button button--primary button--compact" onClick={onResume}>
        {t('game.resume')}
      </button>
    </div>
  )
}

/**
 * Плашка приёма.
 *
 * Название приёма — первое и самое крупное: оно важнее открытой цифры.
 * Цифру игрок и так увидит на доске, а приём — то, ради чего подсказка
 * вообще стоит своих кристаллов.
 */
function TechniquePlaque({
  title,
  message,
  cell,
  onDismiss,
}: {
  title: string
  message: string
  cell: number | null
  onDismiss: () => void
}) {
  return (
    <div className="technique">
      <div className="technique__head">
        <span className="technique__title">{title}</span>
        <span className="technique__tag">{t('technique.tag')}</span>
      </div>
      <p className="technique__body" style={{ margin: 0 }}>
        {message}
      </p>
      {cell !== null && (
        <span className="technique__meta">
          R{Math.floor(cell / 9) + 1}C{(cell % 9) + 1}
        </span>
      )}
      <button type="button" className="button button--primary" onClick={onDismiss}>
        {t('technique.got')}
      </button>
    </div>
  )
}
